/**
 * Token Service for fetching ephemeral tokens from backend
 * 
 * This service handles communication with the backend to obtain
 * short-lived, single-use tokens for the Gemini Live API.
 * Also handles pre-establishing WebSocket connections for instant streaming.
 */

import { TokenResponse } from '../types';
import type { PolicyFlowState, ClaimFlowState } from '../agents/types';
import { GenAILiveClient } from './genai-live-client';
import { config } from '../config/runtime-config';
import {
  beginGlobalApiLoading,
  endGlobalApiLoading,
} from '../utils/globalApiLoading';

/**
 * Context data passed to backend for system instruction injection
 * This preserves conversation state across agent transitions
 */
export interface TokenContextData {
  // Core context - ALL flows
  language?: 'ar' | 'en' | 'ur';
  conversation_summary?: string;
  user_name?: string;
  user_mobile?: string;
  user_location?: string;

  // Ameen Insurance Context - for state continuity
  caller_agent?: string;      // For vehicle inspection return
  flow_state?: PolicyFlowState | ClaimFlowState;
  damage_description?: string; // From vehicle inspection
  return_to_menu?: boolean;   // User returned to main menu (router), not fresh start

  // Policy flow state
  selected_package?: { code: string; price: number };
  customer_data?: any;
  survey_images?: { count: number };

  // Claim flow state
  policy_number?: string;
  claim_request_num?: string;

  // Renewal flow data (from NEW FE template)
  renewal_result?: {
    error?: boolean;
    RequestId?: number;
    RequestDate?: string;
    Message?: string;
    URLMessage?: string;
  };
  report_data?: {
    vehicle_count?: number;
  };
  selected_vehicle?: {
    plate_number?: string;
    plate_category?: string;
  };

  // Search results (from NEW FE template)
  search_results?: {
    count: number;
    report_number?: string;
    report_date?: string;
    plate_number?: string;
    plate_color?: string;
    plate_source?: string;
    plate_category?: string;
  };

  // Accident flow data
  accident_result?: {
    success: boolean;
    report_number?: string;
    message?: string;
  };
  image_analysis?: string;

  // FAQ flow
  pending_question?: string;

  // Resume draft context (for saved drafts / continue later feature)
  resume_draft?: boolean;
  resume_flow_type?: 'policy' | 'claim';
  resume_step?: string;
  completed_documents?: string[];
  completed_underwriting_questions?: string[];
}

/**
 * Request parameters for fetching a Gemini token
 */
export interface TokenRequest {
  agent_id: string;
  voice_gender?: 'male' | 'female';
  context?: TokenContextData;
}

const FLOW_START_AGENTS = ['claim_agent', 'policy_agent'] as const;

/**
 * Context appropriate for a token request for the given agent.
 * Policy and claim flows start at policy_agent/claim_agent — we pass only minimal context so the agent does not skip steps (no other flow's data or chat history).
 */
export function getContextForTokenRequest(agentId: string, fullContext: TokenContextData): TokenContextData {
  if (!FLOW_START_AGENTS.includes(agentId as (typeof FLOW_START_AGENTS)[number])) return fullContext;
  const minimal: TokenContextData = {};
  if (fullContext.language) minimal.language = fullContext.language;
  if (fullContext.user_name) minimal.user_name = fullContext.user_name;
  if (fullContext.user_mobile) minimal.user_mobile = fullContext.user_mobile;
  if (fullContext.user_location) minimal.user_location = fullContext.user_location;
  if (fullContext.return_to_menu !== undefined) minimal.return_to_menu = fullContext.return_to_menu;
  return minimal;
}

/**
 * Backend URL configuration
 * Loaded from config.js (runtime) or .env (build-time)
 */
const BACKEND_URL = config.REACT_APP_BACKEND_URL;

/**
 * Get the backend URL from configuration
 */
export function getBackendUrl(): string {
  if (!BACKEND_URL) {
    throw new Error('REACT_APP_BACKEND_URL is required in config.js file or .env');
  }
  console.log(`🔗 [TOKEN-SERVICE] Using backend URL: ${BACKEND_URL}`);
  return BACKEND_URL;
}

/**
 * Fetch an ephemeral token for a specific agent from the backend
 * 
 * @param backendUrl - The backend API URL
 * @param agentId - The agent ID to get token for
 * @param voiceGender - Voice gender preference ('male' or 'female')
 * @param context - Optional context data to inject into system instruction
 * @returns TokenResponse with token, model, agent_id, and initial_prompt
 * @throws Error if token fetch fails
 */
export async function fetchGeminiToken(
  backendUrl: string,
  agentId: string,
  voiceGender: 'male' | 'female' = 'male',
  context?: TokenContextData
): Promise<TokenResponse> {
  console.log(`🔑 [TOKEN-SERVICE] Fetching ephemeral token for agent: ${agentId}`);
  console.log(`🎤 [TOKEN-SERVICE] Voice gender: ${voiceGender}`);
  if (context?.language) {
    console.log(`🌐 [TOKEN-SERVICE] Language context: ${context.language}`);
  }

  const requestBody: TokenRequest = {
    agent_id: agentId,
    voice_gender: voiceGender,
    context: context,
  };

  beginGlobalApiLoading();
  try {
    const response = await fetch(`${backendUrl}/api/get-gemini-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [TOKEN-SERVICE] Token fetch failed: ${response.status} ${errorText}`);
      throw new Error(`Failed to fetch token: ${response.status} ${errorText}`);
    }

    const data: TokenResponse = await response.json();

    console.log(`✅ [TOKEN-SERVICE] Token fetched successfully for agent: ${data.agent_id}`);
    console.log(`📋 [TOKEN-SERVICE] Model locked to: ${data.model}`);

    return data;
  } catch (error) {
    console.error(`❌ [TOKEN-SERVICE] Error fetching token:`, error);
    throw error;
  } finally {
    endGlobalApiLoading();
  }
}

/**
 * Check if the backend is available
 * 
 * @param backendUrl - The backend API URL
 * @returns true if backend is available, false otherwise
 */
export async function checkBackendHealth(backendUrl: string): Promise<boolean> {
  try {
    const response = await fetch(`${backendUrl}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.ok;
  } catch (error) {
    console.error(`❌ [TOKEN-SERVICE] Backend health check failed:`, error);
    return false;
  }
}

/**
 * Get list of available agents from backend
 * 
 * @param backendUrl - The backend API URL
 * @returns Array of agent IDs
 */
export async function getAvailableAgents(backendUrl: string): Promise<string[]> {
  try {
    const response = await fetch(`${backendUrl}/api/agents`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch agents: ${response.status}`);
    }

    const data = await response.json();
    return data.agents || [];
  } catch (error) {
    console.error(`❌ [TOKEN-SERVICE] Error fetching agents:`, error);
    return [];
  }
}

/**
 * Pre-connected WebSocket client cache for instant streaming
 * The connection is established but audio streaming doesn't start until user clicks "Connect"
 */
let cachedWebSocketClient: GenAILiveClient | null = null;
let cachedWebSocketClientExpiry: number = 0;
let cachedWebSocketModel: string | null = null;
const CONNECTION_CACHE_DURATION = 45000; // 45 seconds (safe margin before token expiry)

/**
 * Pre-establishes WebSocket connection for greeting_agent
 * The connection is ready but audio streaming doesn't start until user clicks "Connect"
 * This eliminates ALL connection latency for instant user experience
 * 
 * @param backendUrl - The backend API URL
 * @param voiceGender - Voice gender preference ('male' or 'female')
 */
export async function prefetchInitialConnection(
  backendUrl: string,
  voiceGender: 'male' | 'female' = 'male'
): Promise<void> {
  try {
    console.log('🚀 [TOKEN-SERVICE] Pre-establishing WebSocket connection for greeting_agent...');

    // Clean up any existing cached connection
    if (cachedWebSocketClient) {
      console.log('🧹 [TOKEN-SERVICE] Cleaning up previous cached connection');
      cachedWebSocketClient.disconnect();
      cachedWebSocketClient = null;
    }

    // Fetch token
    const tokenData = await fetchGeminiToken(backendUrl, 'greeting_agent', voiceGender);

    console.log('🔌 [TOKEN-SERVICE] Establishing WebSocket connection...');

    // Create client with token
    const client = new GenAILiveClient({ token: tokenData.token });

    // Establish WebSocket connection (but don't start audio streaming yet)
    const connected = await client.connect(tokenData.model, {});

    if (!connected) {
      console.error('❌ [TOKEN-SERVICE] WebSocket connection failed');
      client.disconnect();
      return;
    }

    // Cache the connected client
    cachedWebSocketClient = client;
    cachedWebSocketClientExpiry = Date.now() + CONNECTION_CACHE_DURATION;
    cachedWebSocketModel = tokenData.model;

    console.log('✅ [TOKEN-SERVICE] WebSocket pre-connected and ready for instant streaming!');
    console.log('⚡ [TOKEN-SERVICE] User will experience ZERO connection delay!');
  } catch (error) {
    console.error('❌ [TOKEN-SERVICE] Failed to pre-establish connection:', error);
    // Clean up on error
    if (cachedWebSocketClient) {
      cachedWebSocketClient.disconnect();
      cachedWebSocketClient = null;
    }
    // Don't throw - this is just an optimization
  }
}

/**
 * Gets the pre-connected WebSocket client (one-time use)
 * Returns null if cache is expired or not available
 * 
 * @returns Object with client and model, or null
 */
export function getCachedWebSocketClient(): { client: GenAILiveClient; model: string } | null {
  if (!cachedWebSocketClient || !cachedWebSocketModel || Date.now() >= cachedWebSocketClientExpiry) {
    if (cachedWebSocketClient) {
      console.log('⏰ [TOKEN-SERVICE] Cached WebSocket expired, disconnecting');
      cachedWebSocketClient.disconnect();
    }
    cachedWebSocketClient = null;
    cachedWebSocketModel = null;
    return null;
  }

  console.log('⚡ [TOKEN-SERVICE] Using pre-connected WebSocket for INSTANT streaming!');
  const result = { client: cachedWebSocketClient, model: cachedWebSocketModel };
  cachedWebSocketClient = null; // Use once
  cachedWebSocketModel = null;
  return result;
}
